import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import _ from 'lodash';
import homeIcon from "../assets/icons/home.png";
import kikininLogo from '../assets/icons/logo.png';
import storeIcon from '../assets/icons/store.png';
import { svg } from '../assets/svg';
import Enums from '../helpers/enums';
import utils from '../helpers/utils';
import { hooks } from '../hooks';
import { Routes, TabScreens } from '../routes';
import { RootState } from '../store';
import {
  setOpenBottemSheet
} from "../store/slices/appSlice";
import { setCurrentScreen } from '../store/slices/screenSlice';
import { setScreen } from '../store/slices/tabSlice';

type Props = {
  title?: string;
  userRender?: boolean;
  siteRender?: boolean;
  userName?: boolean;
  userPhoto?: boolean;
  showGoBack?: boolean;
  showBasket?: boolean;
  headerStyle?: React.CSSProperties;
  showSkip?: boolean;
  onSkipClick?: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>
  ) => void;
  isShowHome?: boolean;
  userPoints?: boolean;
  logo?: boolean;
  leftLogo?: boolean;
};

export const Header: React.FC<Props> = ({
  title,
  userName,
  userRender,
  userPhoto,
  showGoBack,
  showBasket,
  headerStyle,
  siteRender,
  showSkip,
  onSkipClick,
  userPoints = false,
  isShowHome = false,
  logo,
  leftLogo
}) => {
  const navigate = hooks.useNavigate();
  const location = hooks.useLocation();
  const dispatch = hooks.useDispatch();
  const { user } = useSelector((state: RootState) => state.authReducer.auth);
  const { selectedSiteId } = useSelector((state: RootState) => state.selectedSiteReducer);
  const { sites } = useSelector((state: RootState) => state.screenReducer);
  const totalUserPoints = useSelector((state: RootState) => state.appReducer.userPoints);
  const currentScreen = useSelector((state: RootState) => state.screenReducer.currentScreen);

  const site = useMemo(() => {
    if (!_.isEmpty(sites)) {
      return utils.getSelectedSite(selectedSiteId, sites);
    }
    return {};
  }, [selectedSiteId, sites]);

  const cartItems = useSelector(
    (state: RootState) => state.cartReducer.cartItems
  );

  const totalCheckOutPrice = useMemo(() => {
    let totalPrice = 0;
    totalPrice += _.sumBy(cartItems, (item: any) => {
      if (item.type === Enums.comboType.quickCombo) {
        return (
          (item.price + item.totalCutomizedItemPrice + item.swappedPrice) *
          item.quantity
        );
      } else {
        let itemPrice =
          (item.price + item.totalCutomizedItemPrice) * item.quantity;
        if (item.options && item.options.length > 0) {
          itemPrice += item.options[0].price * item.quantity;
        }
        return itemPrice;
      }
    });
    return Number(totalPrice.toFixed(2));
  }, [cartItems]);

  const renderGoBack = (): JSX.Element | null => {
    if (showGoBack && location.key !== 'default') {
      return (
        <div className='back-button' onClick={() => navigate(-1)} style={{ width: '30px' }}>
          <svg.GoBackSvg />
        </div>
      );
    }
    return null;
  };

  const renderTitle = (): JSX.Element | null => {
    if (!title) return null;
    return (
      <span
        className='t16'
        style={{
          color: 'var(--main-color)',
          marginBottom: 2,
          display: 'block',
        }}
      >
        {title}
      </span>
    );
  };

  const renderSites = (site: any): JSX.Element | null => {
    if (!siteRender || _.isEmpty(site)) return null;
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginRight: 10,
          cursor: 'pointer',
        }}
        onClick={() => {
          if (currentScreen === TabScreens.Home) {
            dispatch(setOpenBottemSheet(true));
          } else {
            dispatch(setScreen(TabScreens.Home));
            dispatch(setCurrentScreen(TabScreens.Home));
            navigate(Routes.TabNavigator);
            dispatch(setOpenBottemSheet(true));
          }
        }}
      >
        <img
          src={storeIcon}
          alt='store-icon'
          style={{ width: 20, height: 20, objectFit: 'contain' }}
        />
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <h3 className='t14' style={{ margin: 0, fontWeight: 600 }}>
            {site.siteName}
          </h3>
          <span className='t10'>
            {`${site.openingTime} - ${site.closingTime}`} |{' '}
            {site.isSiteOpen ? 'Open' : 'Closed'}
          </span>
        </div>
      </div>
    );
  };

  const renderUserPoints = (): JSX.Element | null => {
    return (
      <div style={{
        display: 'flex',
        padding: '4px 8px',
        alignItems: 'center',
        cursor: 'pointer',
        backgroundColor: '#ff161f',
        borderRadius: 20,
      }}
        onClick={() => {
          dispatch(setCurrentScreen(Routes.PointScreen));
          navigate(Routes.PointScreen);
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="#ffffffff"
          style={{ marginRight: '4px' }}
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
        <span className="t14" style={{ color: 'white' }}>{utils.formatNumber(totalUserPoints || 0)}</span>
      </div >
    );
  };

  const renderCenterLogo = (): JSX.Element | null => {
    return (
      <div>
        <img src={kikininLogo} alt={process.env.REACT_APP_NAME} className="logo-image" height={50} />
      </div>
    )
  };

  const renderLeftLogo = (): JSX.Element | null => {
    return (
      <div onClick={() => {
        dispatch(setScreen(TabScreens.Home));
        dispatch(setCurrentScreen(TabScreens.Home));
        navigate(Routes.TabNavigator);
      }}>
        <img src={kikininLogo} alt={process.env.REACT_APP_NAME} className="logo-image" height={50} />
      </div>
    )
  };

  return (
    <>
      <header
        style={{
          height: "var(--header-height)",
          backgroundColor: "var(--white-color)",
          display: "flex",
          alignItems: "center",
          borderRadius: '0px 0px 20px 20px',
          padding: 12,
          justifyContent: 'space-between',
          position: "relative",
          ...headerStyle,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            justifyContent: 'space-between',
          }}
        >
          {renderGoBack()}
          {leftLogo && renderLeftLogo()}
        </div>
        <div
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          {logo && renderCenterLogo()}
          {renderTitle()}
        </div>
        {showSkip ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
            <button
              onClick={onSkipClick}
              className='t14'
              style={{
                height: '100%',
                width: 'auto',
                padding: '0 4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-around',
                position: 'relative',
                color: 'var(--main-turquoise)',
              }}
            >
              Skip
            </button>
          </div>
        ) : null}

        {siteRender || showBasket || isShowHome ? (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {renderSites(site)}
            {user && userPoints ? renderUserPoints() : null}
            {isShowHome && (
              <button onClick={() => {
                dispatch(setScreen(TabScreens.Home));
                dispatch(setCurrentScreen(TabScreens.Home));
                navigate(Routes.TabNavigator)
              }}>
                <img
                  src={homeIcon}
                  alt='store-icon'
                  style={{ width: 24, height: 24, objectFit: 'contain' }}
                />
              </button>
            )}
          </div>
        ) : null}
      </header>
    </>
  );
};
